import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

interface AppMetadata {
  version: string;
  buildNumber: number;
  buildDate: Date;
  fileSize: number;
  fileName: string;
  downloadURL: string;
  releaseNotes: string;
  minAndroidVersion: string;
  packageName: string;
}

const DownloadAppPage: React.FC = () => {
  const [metadata, setMetadata] = useState<AppMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        setLoading(true);

        // First try to fetch the release marked as "latest"
        const latestQuery = query(
          collection(db, 'releases'),
          where('isLatest', '==', true),
          limit(1)
        );
        let snapshot = await getDocs(latestQuery);

        // If no release is marked as latest, get the most recent one
        if (snapshot.empty) {
          const recentQuery = query(
            collection(db, 'releases'),
            orderBy('buildDate', 'desc'),
            limit(1)
          );
          snapshot = await getDocs(recentQuery);
        }

        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setMetadata({
            version: data.version,
            buildNumber: data.buildNumber,
            buildDate: data.buildDate.toDate(),
            fileSize: data.fileSize,
            fileName: data.fileName,
            downloadURL: data.downloadURL,
            releaseNotes: data.releaseNotes,
            minAndroidVersion: data.minAndroidVersion,
            packageName: data.packageName,
          });
          setError(null);
        } else {
          setError('No releases available at this time.');
        }
      } catch (err) {
        console.error('Error fetching release:', err);
        setError('Unable to load app information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  const formatFileSize = (bytes: number): string => {
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes.toFixed(1)} MB`;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownload = () => {
    if (metadata?.downloadURL) {
      // Open download URL in new tab
      window.open(metadata.downloadURL, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading app information...</p>
        </div>
      </div>
    );
  }

  if (error || !metadata) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Download App</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl">
          <div className="flex items-start">
            <svg
              className="w-6 h-6 text-red-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Download Unavailable
              </h3>
              <p className="text-red-700">
                {error ||
                  'The app is not currently available for download. Please contact support.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Download App</h2>

      {/* Main Download Card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                Deepfake Detection App
              </h3>
              <p className="text-gray-600">For Android Devices</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                v{metadata.version}
              </div>
              <div className="text-sm text-gray-500">
                Build {metadata.buildNumber}
              </div>
            </div>
          </div>

          {/* App Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="text-sm text-gray-600 mb-1">File Size</div>
              <div className="font-semibold text-gray-900">
                {formatFileSize(metadata.fileSize)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Build Date</div>
              <div className="font-semibold text-gray-900">
                {formatDate(metadata.buildDate)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Min. Android</div>
              <div className="font-semibold text-gray-900">
                {metadata.minAndroidVersion}+
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Package Name</div>
              <div className="font-semibold text-gray-900 text-xs break-all">
                {metadata.packageName}
              </div>
            </div>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center font-semibold text-lg shadow-md hover:shadow-lg"
          >
            <svg
              className="w-6 h-6 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download APK
          </button>
        </div>
      </div>

      {/* Release Notes */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
        <button
          onClick={() => setShowReleaseNotes(!showReleaseNotes)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
        >
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-gray-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="font-semibold text-gray-900">Release Notes</span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform ${
              showReleaseNotes ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {showReleaseNotes && (
          <div className="px-4 pb-4 border-t border-gray-200">
            <div
              className="pt-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: metadata.releaseNotes }}
            />
          </div>
        )}
      </div>

      {/* Installation Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Installation Instructions
        </h4>
        <ol className="list-decimal list-inside space-y-2 text-blue-900">
          <li>Download the APK file using the button above</li>
          <li>
            Open <strong>Settings</strong> on your Android device
          </li>
          <li>
            Navigate to <strong>Security</strong> or <strong>Apps</strong>
          </li>
          <li>
            Enable <strong>"Install from Unknown Sources"</strong> or{' '}
            <strong>"Allow from this source"</strong>
          </li>
          <li>Locate the downloaded APK file in your Downloads folder</li>
          <li>Tap the file and follow the installation prompts</li>
          <li>Once installed, you can disable "Unknown Sources" if desired</li>
        </ol>
      </div>

      {/* System Requirements */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          System Requirements
        </h4>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Android {metadata.minAndroidVersion} or higher
          </li>
          <li className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Minimum 100 MB free storage space
          </li>
          <li className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Internet connection for app features
          </li>
          <li className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Microphone and phone permissions for call monitoring
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DownloadAppPage;
